import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, User, Settings as SettingsIcon, Shield, Bell, LogOut, Menu, X, BadgeDollarSign, FileDown, Bot, Globe } from "lucide-react";

const NAV = [];

export function Logo({ size = 40 }) {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/tmc-logo.png" alt="TMC" style={{ width: size, height: size }}
        className="rounded-lg object-cover border border-primary/40 shadow-lg shadow-black/50" />
      <div className="leading-none">
        <div className="font-head font-extrabold text-white text-sm tracking-wide"><span className="gold-text">TMC</span> · THE MONEY CLUB</div>
        <div className="text-[10px] text-accent/80 font-mono tracking-wider mt-0.5">SIGNAL MANAGEMENT</div>
      </div>
    </div>
  );
}

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const load = async () => { try { const r = await api.get("/notifications"); setData(r.data); } catch {} };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-lg hover:bg-secondary transition-colors" data-testid="notifications-bell">
        <Bell className="w-5 h-5 text-gray-300" />
        {data.unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-destructive" />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 glass border border-border rounded-lg shadow-xl z-50 p-2" data-testid="notifications-panel">
          <div className="flex justify-between items-center px-2 py-1">
            <span className="text-sm font-semibold text-white">Powiadomienia</span>
            <button onClick={markAll} className="text-xs gold-text hover:underline">Oznacz jako przeczytane</button>
          </div>
          <div className="max-h-80 overflow-y-auto no-scrollbar">
            {data.notifications.length === 0 && <div className="text-xs text-muted-foreground p-3 text-center">Brak powiadomień</div>}
            {data.notifications.map((n) => (
              <div key={n.id} className={`p-2.5 rounded-md mb-1 ${n.read ? "" : "bg-primary/10"}`}>
                <div className="text-sm text-white">{n.title}</div>
                <div className="text-xs text-muted-foreground">{n.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout, isAdmin, canRefer } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/login"); };

  const links = [
    ...(isAdmin ? [{ to: "/bots", key: "bots", label: "Boty sygnałowe", icon: Bot }] : []),
    ...(isAdmin ? [{ to: "/strona", key: "strona", label: "Strona WWW", icon: Globe }] : []),
  ];

  const sidebarContent = (
    <>
      <div className="px-4 py-5"><Logo /></div>
      <nav className="flex-1 overflow-y-auto no-scrollbar px-3 space-y-1 pb-6">
        {links.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setMobileOpen(false)} data-testid={`nav-${it.key}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-primary/15 text-white border border-primary/40" : "text-gray-400 hover:text-white hover:bg-secondary/60 border border-transparent"}`}>
            <it.icon className="w-[18px] h-[18px]" /> {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg" data-testid="nav-profile-card">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-xs font-bold text-black">
            {(user?.first_name?.[0] || user?.username?.[0] || "T").toUpperCase()}
          </div>
          <div className="leading-tight min-w-0">
            <div className="text-sm text-white truncate">{user?.first_name || user?.username}</div>
            <div className="text-[11px] font-mono text-muted-foreground">{user?.tmc_id}</div>
          </div>
        </div>
        <NavLink to="/regulamin" onClick={() => setMobileOpen(false)} data-testid="nav-terms"
          className="flex items-center gap-2 px-2 pt-2.5 mt-1 text-[11px] text-muted-foreground hover:text-white transition-colors">
          <Shield className="w-3.5 h-3.5" /> Regulamin
        </NavLink>

      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background terminal-bg">
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-[260px] bg-[#0A0D14] border-r border-border z-40">
        {sidebarContent}
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/75" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[280px] bg-[#0A0D14] border-r border-border flex flex-col">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-3 p-1 text-gray-400"><X className="w-5 h-5" /></button>
            {sidebarContent}
          </aside>
        </div>
      )}

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 glass border-b border-border">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 text-gray-300" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
              <div className="lg:hidden"><Logo size={34} /></div>
              <div className="hidden lg:flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                <span className="w-2 h-2 rounded-full bg-accent" /> MetaTrader 5 · MetaAPI
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <NotificationsBell />
              <button onClick={doLogout} className="p-2 rounded-lg hover:bg-secondary transition-colors" data-testid="logout-btn"><LogOut className="w-5 h-5 text-gray-300" /></button>
            </div>
          </div>
        </header>

        <main className="p-3 sm:p-4 lg:p-5 max-w-[1800px] mx-auto pb-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
